/**
 * Chainwork Workflow Definitions — ARCH-node-mcp (Core layer)
 * Chain: GT-006 (MCP), FEAT-MCP-001, BET-85
 *
 * Built-in workflows are authored as canonical descriptors, then compiled to the
 * current WorkflowDefinition runtime shape. This keeps runtime behavior stable
 * while giving BET-85 a single registry contract to build on.
 */

import {
  cloneWorkflowDefinition,
  cloneWorkflowDescriptor,
  compileWorkflowDefinition,
  type WorkflowDefinition,
  type WorkflowDescriptor,
} from "./descriptor.js";

export type {
  WorkflowDefinition,
  WorkflowDescriptor,
  WorkflowQuestion,
  WorkflowRound,
} from "./descriptor.js";

// ── Retro Workflow ──────────────────────────────────────────────────────────

const RETRO_WORKFLOW_DESCRIPTOR: WorkflowDescriptor = {
  id: "retro",
  name: "Retrospective",
  shortDescription:
    "Structured team retrospective — reflect, surface patterns, commit to actions. Accepts a decision entry onto the Chain.",
  template: {
    slug: "retro",
    name: "Retrospective",
  },
  icon: "◎",
  facilitatorPreamble: `You are now in **Facilitator Mode**. You are not a coding assistant — you are a facilitator running a structured retrospective.

## Your Behavior

1. **Guide, don't solve.** Ask questions, reflect back, synthesize. Never jump to solutions.
2. **One round at a time.** Complete each round fully before moving to the next. No skipping.
3. **Use structured questions** (AskQuestion tool) for choices and multi-select. Use open conversation for reflection.
4. **Create a Plan** at the start showing all rounds as tasks. Update it as you progress.
5. **Synthesize between rounds.** After collecting input, reflect back what you heard before moving on.
6. **Never go silent.** If something fails, say what happened and what to do next.
7. **Finalize through the workflow substrate.** Complete the terminal round with \`workflows action=checkpoint ... isFinal=true\` so the durable run creates the retro draft record.
8. **Session before checkpoint (default).** Before the **first** \`workflows action=checkpoint\` in this run, ensure an **active** agent write session: MCP \`session action=start\` or \`pb session start\` (DEC-9, STD-135). Inactive or superseded sessions commonly return **400** on checkpoint — durable progress is lost unless the chat record or a **handoff block** preserves it (see handoff skill: Facilitated workflow handoff).
9. **Hand off mid-flight.** If the user switches agents or checkpoint keeps failing, emit the **Facilitated workflow handoff** package from \`.productbrain/skills/handoff.md\` (copy-paste block). Another agent resumes from that block + \`workflows get-run\` or \`start\` with \`restart=true\` — see workflow \`errorRecovery\`.
10. **Match the energy.** Be warm but structured. This is a ceremony, not a checklist.

## Communication Style

- Start each round with its number, name, and a brief instruction
- Use quotes and emphasis to reflect back what the participant said
- End each round with a brief synthesis before transitioning
- When a round is complete, mark it as done in the Plan
- If the participant seems stuck, offer prompts — never pressure
- If a tool call fails, explain what happened and offer an alternative path

## Error Recovery

If at any point a tool call or MCP operation fails:
1. Tell the participant what you were trying to do
2. Explain what went wrong (briefly, no stack traces)
3. Offer a manual alternative (e.g., "I'll capture this in the conversation instead")
4. Continue the workflow — never halt completely

## Plan Structure

Create a Cursor Plan with these rounds as tasks. Mark each in_progress as you enter it, completed when done.`,

  rounds: [
    {
      id: "set-stage",
      num: "01",
      label: "Set the Stage",
      type: "choice",
      instruction:
        "Before we dive in, let's frame what we're reflecting on. What's the scope of this retro?",
      facilitatorGuidance:
        "Start warm. Ask the participant to pick or describe what they're retro-ing. Confirm the scope before proceeding. If they gave context already in their initial message, use it — don't make them repeat.",
      questions: [
        {
          id: "scope",
          prompt:
            "What are we reflecting on? Pick one or describe your own.",
          options: [
            { id: "last-week", label: "Last week's work" },
            { id: "last-sprint", label: "Last sprint/cycle" },
            { id: "specific-project", label: "A specific project or feature" },
            { id: "process", label: "A process or workflow" },
            { id: "custom", label: "Something else — let me describe it" },
          ],
        },
      ],
      outputSchema: {
        field: "scope",
        description: "What timeframe or project is being retro'd",
        format: "freetext",
      },
      maxDurationHint: "2 min",
    },
    {
      id: "what-went-well",
      num: "02",
      label: "What Went Well",
      type: "open",
      instruction:
        "Let's start with the good. What went well? What are you proud of? What should we do more of?",
      facilitatorGuidance:
        "Celebrate. Reflect back each win with genuine emphasis. Ask follow-ups like 'What made that work?' or 'Who else contributed to that?' Collect at least 3 items before synthesizing. Don't rush past the positive — teams skip this too fast.",
      outputSchema: {
        field: "wentWell",
        description: "List of things that went well",
        format: "list",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "what-didnt-go-well",
      num: "03",
      label: "What Didn't Go Well",
      type: "open",
      instruction:
        "Now the harder part. What didn't go well? What frustrated you? Where did things break down?",
      facilitatorGuidance:
        "Create safety. Acknowledge that this is harder. Don't judge or immediately problem-solve — just listen and capture. Ask 'What was the impact of that?' and 'When did you first notice it?'. Reflect back without softening. Collect at least 3 items.",
      outputSchema: {
        field: "didntGoWell",
        description: "List of things that didn't go well",
        format: "list",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "patterns",
      num: "04",
      label: "Patterns & Insights",
      type: "synthesis",
      instruction:
        "Looking at what went well and what didn't — what patterns do you see? What's the deeper insight?",
      facilitatorGuidance:
        "This is YOUR moment as facilitator. Synthesize what you've heard across rounds 2 and 3. Surface themes, connections, and contradictions. Propose 2-3 patterns and ask the participant to react. This round transforms raw observations into actionable insights. Don't let the participant skip the 'why' — push for root causes.",
      outputSchema: {
        field: "patterns",
        description: "Synthesized patterns and insights",
        format: "structured",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "actions",
      num: "05",
      label: "Actions & Commitments",
      type: "commit",
      instruction:
        "Based on these patterns, what will we actually change? Be specific — who does what, by when?",
      facilitatorGuidance:
        "Push for specificity. 'Be better at X' is not an action. 'Randy will set up a 15-min weekly check-in by Friday' is. Each action needs an owner and a deadline. Aim for 2-4 concrete actions. Use AskQuestion to confirm the final list. These become the retro's output.",
      questions: [
        {
          id: "action-confirm",
          prompt: "Are these actions concrete enough to actually happen?",
          options: [
            { id: "yes", label: "Yes — these are clear and actionable" },
            { id: "refine", label: "Let me refine some of these" },
            { id: "add", label: "I want to add more" },
          ],
        },
      ],
      outputSchema: {
        field: "actions",
        description: "Committed actions with owners and deadlines",
        format: "structured",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "close",
      num: "06",
      label: "Close & Capture",
      type: "close",
      instruction:
        "One last thing — in one sentence, what's the single most important thing you're taking away from this retro?",
      facilitatorGuidance:
        "Keep it brief. One sentence reflection. Then summarize the entire retro: scope, key wins, key pain points, patterns identified, actions committed. Finalize the durable run through \`workflows action=checkpoint\` with \`isFinal=true\` so the retro draft record is created via the workflow substrate. Then ask if they want to accept that draft onto the Chain. Thank them for the retro.",
      outputSchema: {
        field: "takeaway",
        description: "Single-sentence takeaway",
        format: "freetext",
      },
      kbCollection: "decisions",
      maxDurationHint: "2 min",
    },
  ],

  output: {
    primaryRecord: {
      routing: {
        mode: "fixed",
        collection: "decisions",
      },
    },
    summaryCapture: {
      createsPrimaryRecord: true,
      nameTemplate: "Retro: {scope} — {date}",
      descriptionField: "rationale",
    },
  },
  runtime: {
    kind: "guided",
  },

  errorRecovery: `If anything goes wrong during the retro:

1. **MCP tool failure**: Skip the workflow finalization step. Summarize everything in the conversation instead. Suggest retrying \`workflows action=checkpoint\` later or capturing manually if needed.
2. **AskQuestion not available**: Fall back to numbered options in plain text. "Reply with 1, 2, or 3."
3. **Plan creation fails**: Continue without the Plan. The conversation IS the record.
4. **Participant goes off-topic**: Gently redirect: "That's valuable — let's capture it. For now, let's stay with [current round]."
5. **Participant wants to stop**: Respect it. Summarize what you have so far. Offer to accept partial results onto the Chain.
6. **Checkpoint failed (e.g. 400, superseded session)**: Say so in one line. **Start or refresh session** (\`session action=start\` / \`pb session start\`), then \`workflows action=get-run\` with the \`runId\` you were given. If the run is **incomplete**, retry \`checkpoint\` with that \`runId\`. If the run is **complete** or **missing**, treat server state as unreliable: run \`workflows action=start workflowId=retro restart=true\` and **paste the Facilitated workflow handoff block** from the prior chat into round 1 so scope and completed rounds are explicit — do not assume partial server progress exists.
7. **Cross-agent resume (default expectation)**: Receiving agent reads the handoff block first. Prefer \`get-run\` to verify status; otherwise **restart** and continue from the **next round** named in the handoff. The conversation + handoff block are valid SSOT when ChainWork did not persist checkpoints (TEN-917).

The retro must never fail silently. Always communicate state.`,
};

// ── Shape Workflow ──────────────────────────────────────────────────────────

const SHAPE_WORKFLOW_DESCRIPTOR: WorkflowDescriptor = {
  id: "shape",
  name: "Shape a Bet",
  shortDescription:
    "Integration-aware shaping: maps the system before designing, traces five paths per element, " +
    "surfaces governance conflicts at design time. Scoring is in the shaping skill; uses capture and session action=commit-constellation for Chain capture.",
  template: {
    slug: "shape-a-bet",
    name: "Shape a Bet",
  },
  icon: "◆",
  facilitatorPreamble: `You are now in **Shaping Mode**. The shaping skill owns the facilitation loop end-to-end — read it for scoring rubrics, investigation patterns, and the capture flow.

Use \`capture\` to create constellation entries (elements, risks, decisions, glossary terms) linked to the bet.
Use \`session action=commit-constellation\` to atomically accept the bet and all linked entries when the shape is complete.
Use \`workflows action=checkpoint\` after each round to persist progress to the durable run.`,

  rounds: [
    {
      id: "context",
      num: "01",
      label: "Gather Context",
      type: "open",
      instruction:
        "Before shaping, let's see what the Chain already knows. Search for related entries, business rules, and existing decisions.",
      facilitatorGuidance:
        "**Investigate proactively.** Spawn sub-agents in parallel: " +
        "(1) `explore` agent to search the codebase for code related to the bet topic — find modules, patterns, and pain points. " +
        "(2) `generalPurpose` agent to search the Chain: call `orient`, use `entries action=search` with keywords, surface related tensions, active bets, and applicable business rules. " +
        "Synthesize both into a 3-5 line context brief: what the codebase reveals, what the Chain knows, what governance applies. " +
        "Present with IDs and file paths. Build a system map: what modules, data flows, and contracts exist in this area today? " +
        "Check governance: do any principles, standards, or business rules constrain this design space? " +
        "Then transition: 'Here's what I found. Does this match the problem you're seeing?'",
      outputSchema: {
        field: "context",
        description: "System map, Chain context, governance constraints, related entries",
        format: "structured",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "framing",
      num: "02",
      label: "Frame the Problem",
      type: "open",
      instruction:
        "What's the problem? Who's affected? What's the workaround today? How much time is this worth?",
      facilitatorGuidance:
        "This round covers problem clarity AND appetite. " +
        "Search the codebase for workarounds, TODOs, and hacks related to the problem — cite what you find as evidence. " +
        "Push with evidence: 'I found 3 workaround patterns in the codebase — this suggests the problem is bigger than it looks.' " +
        "Use AskQuestion for appetite: Small Batch (1-2 weeks) or Big Batch (6 weeks). " +
        "When both dimensions are well-covered, present the frame and ask for Frame Go confirmation. " +
        "If new terms surfaced, capture them to the glossary via `capture`. If tensions surfaced, capture them too.",
      questions: [
        {
          id: "appetite",
          prompt: "How much time is this idea worth? Not how long it'll take — how much are you willing to invest?",
          options: [
            { id: "small", label: "Small Batch — 1-2 weeks, focused scope" },
            { id: "big", label: "Big Batch — 6 weeks, full treatment" },
          ],
        },
        {
          id: "frame-go",
          prompt: "Frame Go — the problem is tight enough to shape a solution. Proceed to elements?",
          options: [
            { id: "go", label: "Frame Go — move to elements" },
            { id: "refine", label: "Refine the frame — something's not right" },
          ],
        },
      ],
      outputSchema: {
        field: "framing",
        description: "Confirmed problem statement and appetite",
        format: "structured",
      },
      kbCollection: "work-packages",
      maxDurationHint: "10 min",
    },
    {
      id: "elements",
      num: "03",
      label: "Find the Elements",
      type: "open",
      instruction:
        "What are the solution elements? Think breadboard level — places, affordances, connections. What's the architecture?",
      facilitatorGuidance:
        "**Investigate first.** Spawn sub-agents to search the codebase for affected modules, existing architecture patterns, and API boundaries. " +
        "Synthesize findings into 3-5 proposed solution elements at breadboard level, citing specific files and modules. " +
        "**Five-path trace per element:** For each element, trace: (1) what data flows through it, (2) what contracts it relies on, " +
        "(3) what contracts it alters, (4) what second-order effects downstream, (5) what happens at boundaries (batch, error, empty, scale). " +
        "Present proposals for user reaction: 'Based on the codebase, here are the elements I'd suggest.' " +
        "For each confirmed element, use `capture` with collection='features' to create a features entry linked to the bet via part_of. " +
        "For Big Batch: also investigate architecture — search for layer boundaries and dependency directions, then propose architecture. " +
        "Check business rules: do any elements conflict with governance surfaced in Round 1? " +
        "Capture new glossary terms and decisions as they surface. " +
        "Aim for 3-5 elements. When elements and architecture are well-covered, present for confirmation.",
      outputSchema: {
        field: "elements",
        description: "Core solution elements and architecture",
        format: "structured",
      },
      kbCollection: "features",
      maxDurationHint: "15 min",
    },
    {
      id: "derisking",
      num: "04",
      label: "De-risk — Rabbit Holes & No-Gos",
      type: "open",
      instruction:
        "What could go wrong? What are we NOT building? Walk through the solution slowly and find the risks.",
      facilitatorGuidance:
        "**Investigate first.** Spawn sub-agents to search the codebase for fragile code, tight coupling, missing tests, performance-sensitive paths, and external dependencies near the affected modules. " +
        "Propose risks with evidence: 'I found that X module has no tests and is tightly coupled to Y — this is a rabbit hole.' " +
        "Every confirmed risk MUST be captured using `capture` with collection='tensions' — each becomes a tensions entry linked to the bet via constrains. " +
        "Push for mitigations — every risk needs a patch or explicit acceptance. " +
        "Then declare no-gos: 'Based on what I've seen, I'd suggest these no-gos.' Every no-go MUST also be captured. " +
        "**Counter-Metric Mandate (STD-19):** If the derisking references any quantitative gates or metrics (accuracy targets, performance budgets, adoption thresholds), " +
        "stress-test them: 'What's the denominator? What gets excluded? What counter-metric would expose a false positive?' " +
        "A metric gate that can be gamed by abstaining or cherry-picking the population is not a gate — name the compound metric that can't be gamed. " +
        "When risks and boundaries are well-covered, present Shape Go gate: 'Is this buildable within the appetite?'",
      questions: [
        {
          id: "shape-go",
          prompt: "Shape Go — the solution fits the appetite and risks are managed. Proceed to validation?",
          options: [
            { id: "go", label: "Shape Go — move to validation" },
            { id: "cut", label: "Cut scope — this grew beyond appetite" },
            { id: "refine", label: "Refine — more de-risking needed" },
          ],
        },
      ],
      outputSchema: {
        field: "derisking",
        description: "Rabbit holes with patches, explicit no-gos",
        format: "structured",
      },
      kbCollection: "tensions",
      maxDurationHint: "10 min",
    },
    {
      id: "validation",
      num: "05",
      label: "Validate & Build Contract",
      type: "synthesis",
      instruction:
        "Check completeness. Generate the build contract. Review the full scorecard.",
      facilitatorGuidance:
        "Review the scorecard from the shaping skill's scoring rubric. Present it as a table. " +
        "Verify the 5 core ingredients: Problem, Appetite, Solution elements, Rabbit Holes, No-Gos. " +
        "For Big Batch: also verify Architecture, narrative hook, and value-flow annotations. " +
        "**System coherence confirmation:** Were all five paths traced for each element? State which interaction risks remain unverified — " +
        "these become explicit risks in the bet, not surprises during implementation. " +
        "**Vision narrative (TEN-281):** Write one paragraph — how does this bet serve the product vision? " +
        "**Test plan (TEN-281):** How will we know this is done? Concrete scenarios, not 'when tests pass.' " +
        "**Counter-Metric Mandate (STD-19):** If the scorecard or build contract contains metrics or gates, verify each has a denominator, a coverage rate, " +
        "and a counter-metric. Flag any gate that can be satisfied by abstaining or excluding the hard cases. " +
        "Present the build contract. Ask: 'Anything to adjust before we capture to the Chain?'",
      outputSchema: {
        field: "validation",
        description: "Completeness check result and build contract",
        format: "structured",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "capture",
      num: "06",
      label: "Capture & Accept",
      type: "close",
      instruction:
        "Capture the pitch to the Chain. Accept draft entries. Connect the constellation.",
      facilitatorGuidance:
        "List all draft entries created during shaping (elements, risks, decisions, glossary terms). " +
        "Ask the user to confirm: 'Ready to accept these onto the Chain?' " +
        "Call `session action=commit-constellation` to atomically accept the bet and all linked entries. " +
        "Then `relations action=suggest entryId='<betId>'` to discover connections. " +
        "Create the top 3 suggested relations via `relations action=batch-create`. " +
        "Thank the user. State: what was shaped, how many constellation entries captured, what the implementer inherits.",
      outputSchema: {
        field: "capture",
        description: "Final capture summary — entries accepted, relations created",
        format: "structured",
      },
      kbCollection: "work-packages",
      maxDurationHint: "5 min",
    },
  ],

  output: {
    primaryRecord: {
      routing: {
        // BET-145 E6 / FEAT-468: shape workflow outputs route to work-packages collection (DEC-245, BET-230 S5).
        mode: "fixed",
        collection: "work-packages",
      },
    },
    emits: [
      {
        kind: "entry",
        collection: "features",
        description: "Solution elements captured during shaping.",
      },
      {
        kind: "entry",
        collection: "tensions",
        description: "Risks and rabbit holes captured during shaping.",
      },
      {
        kind: "entry",
        collection: "decisions",
        description: "Decisions captured inline when they surface.",
      },
      {
        kind: "entry",
        collection: "glossary",
        description: "Glossary terms captured inline when they surface.",
      },
      {
        kind: "update",
        collection: "work-packages",
        description: "Work package status and no-go updates during finalize.",
      },
    ],
  },
  runtime: {
    kind: "guided",
  },

  errorRecovery: `If anything goes wrong during shaping:

1. **facilitate tool failure**: Continue the conversation. The shaping knowledge is in the chat. Suggest retrying the tool call or capturing manually later.
2. **Chain capture failure (duplicate)**: The tool handles duplicates by linking existing entries. If it still fails, note the entry name and move on — capture manually after the session.
3. **Score seems wrong**: Scoring is local to the shaping skill — re-apply the scoring rubric from the shaping SKILL.md against the conversation context. Scores are heuristic; the agent's judgment matters more than the number.
4. **User wants to stop mid-session**: Respect it. Everything captured so far is on the Chain as drafts. Use \`session action=resume\` to pick up later.
5. **MCP server unreachable**: Run the shaping from conversation knowledge. Skip chain capture. Say: "Product Brain is temporarily unavailable — I'll shape from our conversation and we can accept afterward."

The shaping must never fail silently. Always communicate state.`,
};

// ── Implementation Review Workflow ───────────────────────────────────────────
//
// Migration-ready: Same WorkflowDefinition structure as retro/shape.
// When ChainBuilder/processTemplates Phase B adds facilitator workflows,
// this becomes a built-in seed — no redesign needed (DEC-83, TEN-68).
// PRI-5: Workspace-agnostic — uses generic tools (orient, quality, verify,
// library docs via Context7 CLI) that work for any workspace. Not Product-OS-specific.

const IMPLEMENTATION_REVIEW_WORKFLOW_DESCRIPTOR: WorkflowDescriptor = {
  id: "implementation-review",
  name: "Implementation Review",
  shortDescription:
    "Fix-as-you-go implementation review: finds issues, fixes them immediately, verifies, then proceeds. " +
    "Three expert lenses (Staff+ Engineer, CTO, Chain Coherence). Spawns sub-agents for code and test review. " +
    "Extracts learnings into standards/rules. Ends with BET/chain IDs. Accepts an insight onto the Chain.",
  template: {
    slug: "implementation-review",
    name: "Implementation Review",
  },
  icon: "✓",
  facilitatorPreamble: `You are now in **Implementation Review Mode**. You orchestrate a structured review of work that was just built — not a conversation, but a tool-driven audit with sub-agent support.

## Review scope (CRITICAL)

- **Default scope is this conversation only.** Only the work discussed or produced in this conversation is in scope. Do not review or touch files, BETs, or work outside that scope unless the user explicitly expands it (e.g. "also review the auth module").
- **Every file touched must be explicit.** Scope is not complete until you have listed every file that was touched (created, modified, or deleted) in the work under review. Use git status/diff and conversation context to enumerate them. The review then assesses holistic coherence: does this set of changes fit our system, bring us closer to our vision, and make our code and architecture cleaner?
- **Infer scope first.** Use conversation context, git status/diff, and recent edits to determine which BET, feature, or files are in scope. Call orient/start and \`context action=gather\` with the inferred scope; do not ask the user to pick what to review unless it's ambiguous.
- **Ask only to clarify boundaries.** If scope is ambiguous after inference, ask a single, focused question to clarify (e.g. "Should this review include only the changes we just made, or the whole feature?"). Do not ask open-ended "what should we review?" or "which work area?" unless inference failed.
- **State scope explicitly in Round 01.** After orient and context gather, state in one sentence what is in scope (e.g. "Scope: changes in this conversation for BET-72" or "Scope: files X, Y and BET-72"). Confirm only if the user might reasonably expect something else.
- **Sub-agents and tools are scope-bound.** When spawning sub-agents or calling verify/review-against-rules, restrict attention to the scoped work only. Do not audit or suggest changes to code outside the stated scope.

## Your Behavior

1. **Tool-heavy, not chat-heavy.** Each round calls specific tools: orient, quality, verify, chain-review, context action=gather, review-against-rules. Use Context7 **CLI** (\`npx ctx7 library\` then \`npx ctx7 docs\` — project-local after \`npm install\`; avoid cold \`npx ctx7@latest\` in short-timeout shells) for relevant testing and framework docs — not for Chain truth. Do not use Context7 MCP.
2. **Spawn sub-agents** for parallel review: (a) code/architecture review, (b) test honesty (did we edit tests to pass or do they validate real behavior?). Max 2 agents in parallel, 30s budget each. **Constrain each sub-agent to the stated review scope only.**
3. **Fix-as-you-go (FEAT-172).** When you find a HIGH or MEDIUM issue: fix it immediately, verify (lints, types), then proceed. The user sees clean work, not a list of issues. Only defer if estimated >5 min — capture deferred items as tensions. If user says "skip", capture as tension and continue.
4. **Chain is SSOT.** Call orient or start first. Use entries action=search and context action=gather to load relevant BETs, DECs, BRs. If findings conflict with Chain entries, report the conflict and cite the entry ID.
5. **One round at a time.** Complete each round before moving to the next. Checkpoint progress.
6. **Extract learnings (FEAT-174).** After fixing issues, ask: is this a recurring pattern? Offer to capture structural patterns as standards or business rules so future reviews check automatically.
7. **CRITICAL: End with BET/chain IDs.** The final output MUST include a single sentence: "BET/feature IDs reviewed: [IDs]. [One-line summary of findings]."
8. **Never go silent.** If a tool fails, explain and continue. The conversation IS the backup.

## Tool Usage

- \`orient\` or \`start\` — governance, active bets, stale entries
- \`quality action=check entryId="<BET-ID>"\` — score against collection criteria
- \`chain-review action=gate chainEntryId="<id>"\` — coherence gate for chains
- \`quality action=verify-chain\` — glossary code mappings, cross-references vs codebase
- \`review-against-rules\` (domain) — business rule compliance
- \`context action=gather task="implementation review for [BET-ID]"\` — related knowledge
- Context7 CLI — \`npx ctx7 library\` then \`npx ctx7 docs\` for framework/testing docs (not Chain truth); prefer local \`ctx7\` from \`node_modules\` in this monorepo
- \`session action=wrapup-review\` — before close

## Sub-Agent Classification (for test failures)

When reviewing test results: (a) test staleness — code changed, test not updated; (b) regression — code broke behavior; (c) flaky/bad test — harness or timing issue.

## Structured Verdict (CRITICAL)

Round 05 MUST end with a structured verdict block containing exactly these five items:
1. **Verdict:** Y (ship) or N (do not ship) — one word, no hedging.
2. **What shipped:** One sentence describing what was built.
3. **Why it matters:** One sentence on the problem this solves.
4. **User benefit:** One sentence on the value for users.
5. **How to validate:** One sentence on how to verify it works as intended.

This block is the final deliverable of the review. Everything else is supporting evidence.`,

  rounds: [
    {
      id: "orient",
      num: "01",
      label: "Orient & Scope",
      type: "open",
      instruction:
        "Apply the Review scope rules: infer scope from this conversation, git diff, and recent edits (default = work in this conversation only). Enumerate every file touched (created, modified, deleted). Call orient or start, then context action=gather for the inferred BET/feature. State scope in one explicit sentence and list all files in scope. Only ask the participant if scope is ambiguous. The review will assess: holistic coherence with our system, whether this brings us closer to our vision, and whether it makes our code and architecture cleaner.",
      facilitatorGuidance:
        "Infer scope from conversation context, git status/diff, and recent edits — default is work in this conversation only. " +
        "List every file touched (created, modified, or deleted) in the work under review; scope is not complete without this list. " +
        "Call orient or start to load governance and active bets. " +
        "Use context action=gather task='implementation review for [inferred-BET-or-feature]' to load related entries. " +
        "State scope explicitly in one sentence (e.g. 'Scope: changes in this conversation for BET-72'). " +
        "Present: stated scope, the full list of files touched, relevant BETs/DECs/BRs, and any stale entries orient surfaces. " +
        "Frame the review: we will assess holistic coherence with our system, whether this brings us closer to our vision, and whether it makes our code and architecture cleaner. " +
        "Ask the participant only if scope is ambiguous (e.g. 'Only the files we changed, or the whole feature?'). " +
        "Note vision anchors (principles, strategy, active bet constraints) — you will reference these in Rounds 02–05.",
      outputSchema: {
        field: "scope",
        description: "Explicit scope statement, every file touched, BET/feature IDs, related Chain context, vision anchors",
        format: "structured",
      },
      maxDurationHint: "2 min",
    },
    {
      id: "standards-review",
      num: "02",
      label: "Standards & Chain Coherence",
      type: "open",
      instruction:
        "Run quality check, chain-review gate (if applicable), review-against-rules, and codebase coherence gate. " +
        "Is the implementation coherent with the Chain SSOT? Did it introduce or fix registry drift?",
      facilitatorGuidance:
        "For the BET/entry: quality action=check entryId='<ID>'. " +
        "If the work produced a chain artifact: chain-review action=gate. " +
        "Call review-against-rules with the relevant domain (e.g. 'Governance & Decision-Making'). " +
        "**Codebase Coherence Gate (BET-99/FEAT-183):** Compare the Codebase Coherence section from Round 01's orient output " +
        "against the current state. If orient showed coherence gaps and this implementation touched registry files " +
        "(collection colors, routes, classification, renderers, etc.), check whether gaps increased or decreased. " +
        "Gaps increased → flag as 'Coherence: WARN — N new gaps introduced' and list them. " +
        "Gaps decreased → celebrate as 'Coherence: PASS — N gaps fixed.' " +
        "No change → note as 'Coherence: PASS — no change.' " +
        "If Round 01 did not show a coherence section, skip the gate — do not block on missing baseline. " +
        "Present: quality score, gate result, rule compliance, coherence gate verdict.",
      outputSchema: {
        field: "standards",
        description:
          "Quality score, gate result, rule compliance, coherence gate verdict (improved/degraded/unchanged)",
        format: "structured",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "code-and-tests",
      num: "03",
      label: "Code & Test Honesty",
      type: "open",
      instruction:
        "Spawn sub-agents to review implementation and tests for the stated scope only. Fix every HIGH/MEDIUM finding before proceeding. Did we meet high standards? Are tests validating real behavior or did we edit them just to pass?",
      facilitatorGuidance:
        "Restrict all review to the scope stated in Round 01 (by default, work in this conversation only). " +
        "Spawn 1–2 sub-agents in parallel: (1) explore agent — code review, architecture boundaries, type-safety for scoped files only. " +
        "(2) explore agent — test review for scoped code: are tests asserting real behavior or were they edited to pass? " +
        "Pass the scope (files/BET) explicitly to sub-agents so they do not touch other work. " +
        "Use Context7 CLI (npx ctx7 library/docs) for relevant testing and framework best practices if needed. " +
        "Classify any test failures: staleness, regression, or flaky. " +
        "**Fix-as-you-go (FEAT-172):** For every HIGH/MEDIUM finding: fix immediately, run lints, verify types. " +
        "Only defer fixes estimated >5 min — capture deferred items as tensions on the Chain. " +
        "After fixing, do a boy-scout pass on each scoped file: clean imports, improve naming, remove dead code. " +
        "**Counter-Metric Mandate (STD-19):** When reporting test coverage, accuracy, or any quantitative result, " +
        "include the denominator, what was excluded, and at least one counter-metric. " +
        "Never report accuracy without recall. Never report pass rate without coverage. " +
        "Synthesize: findings, fixes applied, implementation grade, test honesty verdict.",
      outputSchema: {
        field: "codeAndTests",
        description: "Implementation grade, test honesty, refactor suggestions",
        format: "structured",
      },
      maxDurationHint: "10 min",
    },
    {
      id: "chain-validation",
      num: "04",
      label: "Chain Validation & Learning Extraction",
      type: "synthesis",
      instruction:
        "Run verify. Check orient for stale entries. Is the Chain current with the codebase? " +
        "Then extract learnings: did this review surface structural patterns that should become standards or rules?",
      facilitatorGuidance:
        "**Chain validation:** Call quality action=verify-chain (report mode) — checks glossary code mappings and cross-references. " +
        "Orient surfaces stale entries (per workspace governance). Present trust score and any drift. " +
        "If drift found: fix immediately via entries action=update (with changeNote). " +
        "**Learning extraction (FEAT-174, TEN-272):** Review all findings from Rounds 02 and 03. " +
        "Ask: did this review surface a structural pattern that could recur? " +
        "For each pattern: could it be a standard ('We always do X this way'), a business rule ('The system must do X'), " +
        "or a lint rule (automated check)? Offer to capture. " +
        "Also offer to capture any decisions, tensions, or new terms surfaced during the review. " +
        "The goal: today's bug becomes tomorrow's automated check (TEN-272).",
      outputSchema: {
        field: "chainValidation",
        description: "Verify trust score, drift fixes, extracted learnings (patterns → standards/rules)",
        format: "structured",
      },
      maxDurationHint: "5 min",
    },
    {
      id: "synthesis",
      num: "05",
      label: "Synthesis & Sign-Off",
      type: "commit",
      instruction:
        "Synthesize the review. Answer: Does this bring us closer to our vision and make our code and architecture cleaner? " +
        "With fix-as-you-go, most issues are already resolved — summarize what was fixed, not what's left. End with BET/chain IDs.",
      facilitatorGuidance:
        "Present the full synthesis: standards pass/fail, code grade, test honesty, chain validation, fixes applied. " +
        "**Vision delta:** Cite specific principles/strategy entries from Round 01. Answer: does this bring us closer to [cited vision]? " +
        "**Counter-Metric Mandate (STD-19):** For EVERY quantitative result you present, you MUST include: " +
        "(1) the denominator — what is the total population measured, (2) the coverage/abstention rate — what was excluded, " +
        "(3) at least one counter-metric that tells the opposite story (recall vs precision, error rate vs success rate), " +
        "(4) the 'student exam' reframe — score on the full exam, not just questions answered. " +
        "**Red-team your own synthesis:** Before presenting, ask yourself: 'What's the worst honest interpretation of these numbers? " +
        "What metric looks good but hides a real problem? What would a skeptic challenge?' Present that alongside the headline. " +
        "Recommend: ship, ship with conditions, or do not ship. " +
        "CRITICAL: The output MUST end with one sentence: 'BET/feature IDs reviewed: [IDs]. [One-line summary].' " +
        "Example: 'BET-xxx, STD-xxx reviewed. Conditional ship — fix [specific issues] and add [missing tests].' " +
        "**Structured Verdict Block (REQUIRED):** After the full synthesis, present the verdict block with exactly 5 items: " +
        "(1) Verdict: Y or N — one word. (2) What shipped: one sentence. (3) Why it matters: one sentence on the problem solved. " +
        "(4) User benefit: one sentence on value for users. (5) How to validate: one sentence on verification. " +
        "This block is the final deliverable. Everything else is supporting evidence.",
      outputSchema: {
        field: "synthesis",
        description: "Full review synthesis with vision delta, fixes applied, BET/chain IDs, and structured verdict block",
        format: "structured",
      },
      maxDurationHint: "3 min",
    },
    {
      id: "capture",
      num: "06",
      label: "Capture & Close",
      type: "close",
      instruction:
        "Finalize the review as an insight draft. Connect new entries from learning extraction. Use session action=wrapup-review. Thank the participant.",
      facilitatorGuidance:
        "Summarize the review for finalization. Complete the terminal round through \`workflows action=checkpoint\` with \`isFinal=true\` so the workflow substrate creates the draft insight record. " +
        "Include: BET/feature IDs, verdict, key findings, fixes applied, and the structured verdict block from Round 05. " +
        "The insight description MUST include the 5-item verdict block (verdict Y/N, what shipped, why it matters, user benefit, how to validate). " +
        "If learnings were captured in Round 04: run relations action=suggest for each new entry, then relations action=batch-create. " +
        "Run session action=wrapup-review before closing. " +
        "Thank the participant. State what was fixed, what was captured, what was deferred.",
      outputSchema: {
        field: "capture",
        description: "Insight entry created, BET IDs referenced, learnings connected",
        format: "structured",
      },
      kbCollection: "insights",
      maxDurationHint: "3 min",
    },
  ],

  output: {
    primaryRecord: {
      routing: {
        mode: "fixed",
        collection: "insights",
      },
    },
    summaryCapture: {
      createsPrimaryRecord: true,
      nameTemplate: "Implementation Review — {date}",
      descriptionField: "description",
      descriptionSource: {
        mode: "step-output-field",
        field: "synthesis",
      },
    },
  },
  runtime: {
    kind: "guided",
  },

  errorRecovery: `If anything goes wrong during implementation review:

1. **Tool failure**: Explain what failed. Continue with what you have. The conversation is the record.
2. **Sub-agent timeout**: Proceed without that agent's output. Note what wasn't checked.
3. **Context7 CLI unavailable** (e.g. auth/network): Skip library doc validation. Rely on Chain and codebase review.
4. **MCP unreachable**: Run from conversation knowledge. Summarize findings. Offer to capture manually later.

The review must never fail silently. Always end with BET/chain IDs.`,
};

// ── Process Change Workflow ──────────────────────────────────────────────────
//
// BET-84: The lightweight everyday workflow for work that isn't shaping.
// Bug fixes, change requests, improvements, prerequisite tasks.
// Captures why/who/when without a full shaping ceremony.
//
// Migration-ready: Same WorkflowDefinition structure (DEC-83 Phase A).
// PRI-5: Workspace-agnostic — uses only generic tools (orient, capture,
// checkpoint). No workspace-specific logic or entry IDs.
// DEC-149: No facilitate dependency — generic tools only.

const PROCESS_CHANGE_WORKFLOW_DESCRIPTOR: WorkflowDescriptor = {
  id: "process-change",
  name: "Process a Change",
  shortDescription:
    "Lightweight workflow for everyday changes — bug fixes, improvements, change requests. " +
    "Captures what changed, why, and what's affected without a full shaping ceremony. " +
    "Routes output to the right collection via the classifier.",
  template: {
    slug: "process-change",
    name: "Process a Change",
  },
  icon: "↻",
  facilitatorPreamble: `You are now in **Change Processing Mode**. You are a lightweight facilitator helping the user capture the context around a change they're making or just made — a bug fix, improvement, change request, or prerequisite task.

## Your Behavior

1. **Be fast.** This is a 5-minute workflow, not a 45-minute ceremony. Respect the user's flow state.
2. **Orient first.** Call orient to load workspace context — but present only what's relevant to the change, not the full briefing.
3. **Capture the why.** The single most important thing: why is this change happening? The what is usually obvious from the code. The why is what evaporates.
4. **Link to what exists.** Search the Chain for related entries. If this change was triggered by a tension, bet, or decision — link it. If it creates a new decision or surfaces a new tension — capture that too.
5. **Finalize through the workflow substrate.** Complete the terminal round with \`workflows action=checkpoint ... isFinal=true\`. The substrate will classifier-route the primary record and attach it to the durable run.
6. **Don't over-process.** If the user gives you everything in one message, move quickly through the required checkpoints in order, then finalize. Not every round needs a conversation.
7. **End with the link.** Always finish by connecting the new entry to related Chain knowledge via graph suggest.

## Tool Usage

- \`orient\` — load workspace context (governance, active bets, stale entries)
- \`entries action=search\` — find related entries
- \`capture\` — manual fallback only if workflow finalization cannot safely route the entry
- \`relations action=suggest\` — discover connections
- \`relations action=create\` or \`relations action=batch-create\` — link to related entries
- \`entries action=commit\` — promote to SSOT (only on user confirmation)
- \`workflows action=checkpoint\` — save progress if needed

## Communication Style

- **Conversational, not ceremonial.** No round headers needed unless the user wants structure.
- **One question at a time.** Ask what you need, checkpoint, move on.
- **If the user gives rich context upfront**, checkpoint the answered rounds in order, then finalize and link. Don't ask questions they already answered.
- **Show what you captured.** Always confirm what went to the Chain before closing.`,

  rounds: [
    {
      id: "orient",
      num: "01",
      label: "Quick Context",
      type: "open",
      instruction:
        "What are you working on? Orient to load relevant Chain context.",
      facilitatorGuidance:
        "Call orient to get workspace context. Ask what the user is changing — or infer from their initial message. " +
        "Search the Chain for related entries: entries action=search with keywords from their description. " +
        "Present only the 2-3 most relevant entries, not a full briefing. If the user already described the change, skip the question and confirm what you understood.",
      outputSchema: {
        field: "context",
        description: "What's being changed and relevant Chain context",
        format: "structured",
      },
      maxDurationHint: "1 min",
    },
    {
      id: "what-and-why",
      num: "02",
      label: "What & Why",
      type: "open",
      instruction:
        "What's changing and why? What triggered this — a bug, a request, an observation?",
      facilitatorGuidance:
        "This is the core capture moment. Get two things: (1) what changed or will change, (2) why — the trigger, the reasoning, the context that will evaporate. " +
        "If the user's initial message already contains both, reflect it back for confirmation rather than re-asking. " +
        "Classify the change type mentally: bug fix → tension, decision made → decision, new capability → feature, lesson learned → insight. " +
        "The classifier will handle routing, but understanding the type helps you ask the right follow-up.",
      outputSchema: {
        field: "whatAndWhy",
        description: "The change and its rationale",
        format: "freetext",
      },
      maxDurationHint: "2 min",
    },
    {
      id: "impact-and-links",
      num: "03",
      label: "Impact & Links",
      type: "synthesis",
      instruction:
        "What's affected? Are there existing Chain entries that relate to this change?",
      facilitatorGuidance:
        "Search the Chain for entries that should be linked: entries action=search with keywords from the change. " +
        "Check: does this change affect an active bet? Does it resolve a known tension? Does it supersede an existing decision? " +
        "If the change is small and self-contained, this round can be brief — just confirm there's nothing obvious to link. " +
        "If there are stale entries that this change invalidates, flag them for reconfirmation.",
      outputSchema: {
        field: "impact",
        description: "Affected entries and proposed links",
        format: "structured",
      },
      maxDurationHint: "1 min",
    },
    {
      id: "capture-and-close",
      num: "04",
      label: "Capture & Close",
      type: "close",
      instruction:
        "Capture to the Chain. Link to related entries. Confirm with the user.",
      facilitatorGuidance:
        "Finalize through workflows action=checkpoint with isFinal=true, passing a summaryName when you have a clean title. " +
        "The workflow substrate will create the primary record as a draft and attach it to the durable run. " +
        "After finalization, call relations action=suggest to find connections. Create the top 2-3 relevant relations. " +
        "Show the user what was captured: entry ID, collection it landed in, relations created. " +
        "Ask if they want to accept now or leave as draft. If they confirm, use entries action=commit. " +
        "If there are stale entries to reconfirm, mention them. " +
        "Thank the user briefly and exit — don't linger.",
      outputSchema: {
        field: "capture",
        description: "Entry captured, relations created, accept status",
        format: "structured",
      },
      maxDurationHint: "1 min",
    },
  ],

  output: {
    primaryRecord: {
      routing: {
        mode: "classifier",
      },
    },
    summaryCapture: {
      createsPrimaryRecord: true,
      nameTemplate: "{whatAndWhy}",
      descriptionSource: {
        mode: "step-output-field",
        field: "whatAndWhy",
      },
    },
  },
  runtime: {
    kind: "guided",
  },

  errorRecovery: `If anything goes wrong during change processing:

1. **Tool failure**: Explain briefly. Capture in the conversation. Offer to retry or accept manually later.
2. **Classifier misroutes**: Use entries action=move to move the entry to the correct collection. Mention this to the user.
3. **No related entries found**: That's fine — not every change connects to existing knowledge. Capture it standalone.
4. **MCP unreachable**: Note the change in conversation. Offer to capture when PB is back.

Never block the user's real work. This workflow serves the work — it doesn't replace it.`,
};

// ── Workflow Registry ───────────────────────────────────────────────────────

const WORKFLOW_DESCRIPTORS = new Map<string, WorkflowDescriptor>([
  ["retro", RETRO_WORKFLOW_DESCRIPTOR],
  ["shape", SHAPE_WORKFLOW_DESCRIPTOR],
  ["implementation-review", IMPLEMENTATION_REVIEW_WORKFLOW_DESCRIPTOR],
  ["process-change", PROCESS_CHANGE_WORKFLOW_DESCRIPTOR],
]);

const WORKFLOWS = new Map<string, WorkflowDefinition>(
  Array.from(WORKFLOW_DESCRIPTORS.entries(), ([id, descriptor]) => [
    id,
    compileWorkflowDefinition(descriptor),
  ]),
);

export function getWorkflow(id: string): WorkflowDefinition | undefined {
  const workflow = WORKFLOWS.get(id);
  return workflow ? cloneWorkflowDefinition(workflow) : undefined;
}

export function listWorkflows(): WorkflowDefinition[] {
  return Array.from(WORKFLOWS.values(), (workflow) =>
    cloneWorkflowDefinition(workflow),
  );
}

export function getWorkflowDescriptor(id: string): WorkflowDescriptor | undefined {
  const descriptor = WORKFLOW_DESCRIPTORS.get(id);
  return descriptor ? cloneWorkflowDescriptor(descriptor) : undefined;
}

export function listWorkflowDescriptors(): WorkflowDescriptor[] {
  return Array.from(WORKFLOW_DESCRIPTORS.values(), (descriptor) =>
    cloneWorkflowDescriptor(descriptor),
  );
}

export function getWorkflowIds(): string[] {
  return Array.from(WORKFLOWS.keys());
}
